/**
 * Host Hunt Print Page
 *
 * Allow the host to see all of the hunt information, including all of the
 * questions, their answers, and live team submissions.
 */
import React from 'react'
import { navigate } from '../../utils/navigate'
import { isHostLoggedIn } from '../../utils/host-auth'
import API from '../../utils/api'
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import { b64DecodeUnicode } from '../../utils/helpers'
import { getTeamAnswerLines } from '../../utils/host-print-answers'

dayjs.extend(LocalizedFormat)

const PRINT_POLL_INTERVAL_MS = 3000

const decodeAnswers = crypt => (crypt ? b64DecodeUnicode(crypt) : ``)

const renderPrintQuestion = (
  question,
  index,
  teams,
  { keyPrefix = `question`, showClockwiseDirections = true, answersField = `answers` } = {}
) => {
  const teamLines = getTeamAnswerLines(teams, question.id, answersField)

  return (
    <li className="question mb-4" key={`${keyPrefix}-${index}`}>
      <strong>{index + 1}. Location: </strong>
      {question.location}
      <br />
      {showClockwiseDirections ? (
        <>
          <strong>Directions Clockwise: </strong>
          <div dangerouslySetInnerHTML={{ __html: question.directions_clockwise }} />
        </>
      ) : (
        question.directions_simple && (
          <>
            <strong>Directions: </strong>
            {question.directions_simple}
            <br />
          </>
        )
      )}
      <div>
        <strong>Question: </strong>
        <span className="print-item" dangerouslySetInnerHTML={{ __html: question.question }} />
      </div>
      <div>
        <strong>Hint: </strong>
        <span className="print-item" dangerouslySetInnerHTML={{ __html: question.hint }} />
      </div>
      <strong>Answers: </strong>
      {decodeAnswers(question.answers_aes_crypt)}
      <br />
      {teamLines.length > 0 && (
        <>
          <strong>Team Answers:</strong>
          {teamLines.map((line, lineIndex) => (
            <div key={`${keyPrefix}-${index}-team-${lineIndex}`}>{line}</div>
          ))}
        </>
      )}
      <strong>Point Value:</strong> {question.answer_value} Full / {question.hint_value} Partial
    </li>
  )
}

export default class HostPrintPage extends React.Component {
  constructor(props) {
    super(props)
    this.updateSelf = this.updateSelf.bind(this)
    this.state = {
      hunt: null,
      is_updating: false,
    }
  }

  componentDidMount() {
    // Verify auth once on mount. Re-verifying on every poll was wiping the
    // shared host JWT on transient network errors and forcing re-login mid-hunt.
    isHostLoggedIn().then(loggedIn => {
      if (!loggedIn) {
        navigate(`/host/login/`)
        return
      }
      this.updateSelf()
      this.interval = setInterval(this.updateSelf, PRINT_POLL_INTERVAL_MS)
    })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.hash !== this.props.location.hash) {
      this.updateSelf()
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval)
  }

  updateSelf() {
    const hunt_id = this.props.location.hash.substring(1)
    if (!hunt_id || this.state.is_updating) {
      return
    }

    this.setState({ is_updating: true })
    API.get(`/host/hunt/${hunt_id}/`, {
      headers: {
        Authorization: `JWT ${localStorage.getItem(`token`)}`,
      },
      // Give a slow connection time to finish, then release the lock so the
      // next poll can try again instead of freezing the open script.
      timeout: 20000,
    })
      .then(res => {
        this.setState({ hunt: res.data, is_updating: false })
      })
      .catch(
        function(error) {
          console.log(error)
          this.setState({ is_updating: false })
          const status = error && error.response && error.response.status
          // A slow or dropped refresh used to close this page (and, on 401,
          // wipe the shared host login). Stay on the script and try again
          // unless the server actually rejected the login.
          if (status === 401 || status === 403) {
            localStorage.removeItem(`token`)
            navigate(`/host/login/`)
          }
        }.bind(this)
      )
  }

  render() {
    const { hunt } = this.state
    const questions = hunt && hunt.questions ? hunt.questions : []
    const secondaryQuestions = hunt && hunt.secondary_questions ? hunt.secondary_questions : []
    const checklists = hunt && hunt.checklists ? hunt.checklists : []
    const teams = hunt && hunt.teams ? hunt.teams : []

    return (
      <div className={`page-wrap print-view`}>
        {hunt && (
          <div className="py-5 container">
            <div className="card px-3 py-4">
              <h1>{hunt.title}</h1>
              <h3>{hunt.location}</h3>
              <div>
                <strong>Hunt Type: </strong>
                {hunt.hunt_type == 1 ? `Grab & Go` : `Default`}
              </div>
              <div>
                <strong>Game Notes: </strong> <div dangerouslySetInnerHTML={{ __html: hunt.description }} />
              </div>
              <div>
                <strong>Start: </strong> {dayjs(hunt.start).format(`L LT`)}
              </div>
              <div>
                <strong>Duration: </strong> {hunt.duration}
              </div>
              <div>
                <strong>Stop Word:</strong> {hunt.stop_word}
              </div>
              <div>
                <strong>Client:</strong> {hunt.client}
              </div>
              <div>
                <strong>Play Sounds:</strong> {hunt.play_sounds ? `yes` : `no`}
              </div>
              <div>
                <strong>Hints Enabled:</strong> {hunt.enable_hints ? `yes` : `no`}
              </div>
              <div>
                <strong>Show Answers Enabled:</strong> {hunt.enable_answers ? `yes` : `no`}
              </div>
              <div>
                <strong>Timer Enabled:</strong> {hunt.enable_timer ? `yes` : `no`}
              </div>
              <div>
                <strong>Photos Enabled:</strong> {hunt.enable_photos ? `yes` : `no`}
              </div>
              <div>
                <strong>Photo Gallery Enabled:</strong> {hunt.enable_gallery ? `yes` : `no`}
              </div>
              <div>
                <strong>Rules:</strong> <div dangerouslySetInnerHTML={{ __html: hunt.rules }} />
              </div>

              <h3>Questions</h3>
              {questions.length === 0 ? (
                <p>No questions configured for this hunt.</p>
              ) : (
                <ol>{questions.map((question, index) => renderPrintQuestion(question, index, teams))}</ol>
              )}

              {secondaryQuestions.length > 0 && (
                <>
                  <h3>{hunt.secondary_questions_title || `Secondary Questions`}</h3>
                  <ol>
                    {secondaryQuestions.map((question, index) =>
                      renderPrintQuestion(question, index, teams, {
                        keyPrefix: `secondary-question`,
                        showClockwiseDirections: false,
                        answersField: `secondary_answers`,
                      })
                    )}
                  </ol>
                </>
              )}

              {checklists.length > 0 && (
                <>
                  <h3>Checklists</h3>
                  <ol>
                    {checklists.map((checklist, index) => (
                      <li className="checklist mb-4" key={`checklist-${index}`}>
                        <strong>Title: </strong>
                        {checklist.title}
                        <br />
                        <strong>Checklist Type: </strong>
                        {checklist.type}
                        <br />
                        <strong>Display Title: </strong>
                        {checklist.display_title}
                        <br />
                        <strong>Maxiumum Score: </strong>
                        {checklist.maximum_score}
                        <br />
                        <div>
                          <strong>Description: </strong>
                          <span className="print-item" dangerouslySetInnerHTML={{ __html: checklist.description }} />
                        </div>
                        <strong>Checklist Questions:</strong>
                        <ol>
                          {checklist.questions.map(c_question => (
                            <li key={`checklist-question-${c_question.id}`}>
                              <strong>Title: </strong>
                              {c_question.title}
                              <br />
                              <strong>Description: </strong>
                              {c_question.description}
                              <br />
                              <strong>Point Value: </strong>
                              {c_question.answer_value}
                            </li>
                          ))}
                        </ol>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
}
